import { type FC, useContext, useEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import { unknownErrorMessage } from '../../../constants/generalData';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import { TemporaryAccessCodeStatus } from '../../../modules/enums/status';
import {
  useAuth,
  useLoading,
  useMutationRequest,
  useNotifications,
  useQueryRequest,
} from '../../../modules/hooks';
import type { RoomLocks, TemporaryAccessCodes } from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import Header from '../../Header';
import { AddTemporaryAccessCodeModal } from '../../Modals/TempararyAccessCodeModal/AddTemporaryAccessCode';
import { TemporaryAccessCodeTable } from '../../Tables/TemporaryAccessCode';
import EditTemporaryAccessCodeStatus from './EditTemporaryAccessCodeStatus';
import { ViewTemporaryAccessCodeModal } from './ViewTemporaryAccessCodeModal';

export const TemporaryAccessCode: FC = () => {
  const { setSimpleToasts } = useNotifications()!;
  const [addTemporaryAccessCodeModal, setAddTemporaryAccessCodeModal] = useState(false);
  const [defaultTemporaryAccessCode, setDefaultTemporaryAccessCode] = useState<
    TemporaryAccessCodes | undefined
  >(undefined);
  const [temporaryAccessCodeData, setTemporaryAccessCodeData] =
    useState<TemporaryAccessCodes[]>();

  const { credentialsInfo } = useAuth()!;
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [showDisableAccessCodeModal, setShowDisableAccessCodeModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [temporaryAccessCodeId, setTemporaryAccessCodeId] = useState('');
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const [tempAccessCodeStatus, setTempAccessCodeStatus] =
    useState<TemporaryAccessCodeStatus>(TemporaryAccessCodeStatus.ENABLED);

  const lockUrl = `${endpoints.LOCKS}/temporary-access-code`;
  const {
    data: locksResult,
    refetch: getTemporaryAccessCodes,
    loading: locksLoading,
    error: locksError,
  } = useQueryRequest<TemporaryAccessCodes[]>(lockUrl, {
    authToken: credentialsInfo?.token,
    propertyId: selectedProperty?.property.uuid,
  });

  const updateTempAccessCodeUrl = `${endpoints.LOCKS}/temporary-access-code/${defaultTemporaryAccessCode?.uuid}`;
  const [
    {
      data: updateAccessCodeResult,
      loading: loadingUpdateAccessCode,
      error: errorUpdateAccessCode,
    },
    updateTemporaryAccessCode,
  ] = useMutationRequest<RoomLocks[]>(
    updateTempAccessCodeUrl,
    'put',
    {
      authToken: credentialsInfo?.token,
      propertyId: selectedProperty?.property.uuid,
    },
    { manual: true },
  );

  const onAddTemporaryAccessCode = () => {
    setAddTemporaryAccessCodeModal(true);
  };

  const onDisable = (temporaryAccessCode?: TemporaryAccessCodes) => {
    if (temporaryAccessCode?.status && temporaryAccessCode?.uuid) {
      setTempAccessCodeStatus(temporaryAccessCode?.status);
      setDefaultTemporaryAccessCode(temporaryAccessCode);
    }

    setShowDisableAccessCodeModal(true);
  };

  const onRefresh = () => {
    getTemporaryAccessCodes();
  };

  const onEditTemporaryAccessCode = (temporaryAccessCode?: TemporaryAccessCodes) => {
    setDefaultTemporaryAccessCode(temporaryAccessCode);
    setIsEdit(true);
    setAddTemporaryAccessCodeModal(true);
  };

  const OnViewAccessCodeModal = (temporaryAccessCode?: TemporaryAccessCodes) => {
    setTemporaryAccessCodeId(temporaryAccessCode?.uuid || '');
    setShowAccessCodeModal(true);
  };

  const disableTempAccessCode = (tempAccessCodeStatus: TemporaryAccessCodeStatus) => {
    if (tempAccessCodeStatus) {
      const payload = { status: tempAccessCodeStatus };

      updateTemporaryAccessCode(payload);
    }
  };

  useEffect(() => {
    if (selectedProperty) {
      getTemporaryAccessCodes();
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (locksResult) {
      setTemporaryAccessCodeData(locksResult?.result);
    }

    if (locksError) {
      setSimpleToasts({
        message: locksError?.message || unknownErrorMessage,
        type: 'danger',
        show: true,
      });
    }
  }, [locksResult, locksError]);

  useEffect(() => {
    if (updateAccessCodeResult) {
      setShowDisableAccessCodeModal(false);
      getTemporaryAccessCodes();
      setDefaultTemporaryAccessCode(undefined);
    }

    if (errorUpdateAccessCode) {
      setSimpleToasts({
        message:
          errorUpdateAccessCode?.response?.data.error.message || unknownErrorMessage,
        type: 'danger',
        show: true,
      });
    }
  }, [updateAccessCodeResult, errorUpdateAccessCode]);

  const { setLoading } = useLoading()!;
  useEffect(() => {
    setLoading(loadingUpdateAccessCode);
  }, [loadingUpdateAccessCode]);

  return (
    <Container fluid className="article-page-container p-0">
      <Header title={`Temporary Access Codes`} />

      <TemporaryAccessCodeTable
        addButtonFunction={onAddTemporaryAccessCode}
        addButtonText="Add Temporary Access Code"
        onRefresh={onRefresh}
        tableData={temporaryAccessCodeData}
        onViewAccessCode={OnViewAccessCodeModal}
        onEditTemporaryAccessCode={onEditTemporaryAccessCode}
        onDisable={onDisable}
      />
      <AddTemporaryAccessCodeModal
        show={addTemporaryAccessCodeModal}
        handleClose={() => {
          setAddTemporaryAccessCodeModal(false);
          setDefaultTemporaryAccessCode(undefined);
          setIsEdit(false);
        }}
        onRefresh={onRefresh}
        defaultTemporaryAccessCode={defaultTemporaryAccessCode}
        isEdit={isEdit}
      />
      {temporaryAccessCodeId && (
        <ViewTemporaryAccessCodeModal
          show={showAccessCodeModal}
          handleClose={() => setShowAccessCodeModal(false)}
          temporaryAccessCodeId={temporaryAccessCodeId}
        />
      )}

      <EditTemporaryAccessCodeStatus
        show={showDisableAccessCodeModal}
        onHide={() => setShowDisableAccessCodeModal(false)}
        title="Change temporary access code status"
        description={
          tempAccessCodeStatus === TemporaryAccessCodeStatus.ENABLED
            ? 'Do you want to disable this temporary access code?'
            : 'Do you want to enable this temporary access code?'
        }
        onDisabled={disableTempAccessCode}
        isTempAccessCodeDisabled={tempAccessCodeStatus}
      />
    </Container>
  );
};
